import React, { useState } from "react";
import styled from "styled-components";
import { useScrollPosition } from "@n8tb1t/use-scroll-position";
import { graphql, Link, StaticQuery } from "gatsby";
import Img from "gatsby-image/withIEPolyfill";
import ReactModal from "./ReactModal";
import MobileMenu from "./mobileMenu";

// const HEADER_HEIGHT = "4rem";
// const THRESHOLD = "32";

const OuterContainer = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  justify-content: center;
  z-index: 1100;
`;

const InnerContainer = styled.div`
  width: 100%;
  max-width: 1500px;
  background: #fff;
  height: ${(props) => props.theme.headerHeight};
  transition: 250ms ease-in-out;
  display: flex;
  position: relative;
  padding: 0 1rem;

  @media screen and (min-width: 600px) {
    padding: 0 2rem;
  }
`;

const LogoContainer = styled.div`
  width: 7rem;
  padding: 1.125rem 0;
  position: relative;
`;

const Spacer = styled.div`
  height: ${(props) => props.theme.headerHeight};
  background-color: ${(props) => props.theme.htmlBackgroundColor};
`;

// const LargeNav = styled.nav`
//   display: none;
//   @media screen and (min-width: 900px) {
//     display: flex;
//     align-items: center;
//     margin-left: 1rem;
//   }
// `;
//
// const LargeNavLink = styled(Link)`
//   padding: 0.5rem;
//   display: block;
//
//   &.active {
//     color: ${(props) => props.theme.bodySecondaryColor};
//     //font-weight: bold;
//   }
//
//   @media screen and (min-width: 1200px) {
//     padding-left: 1rem;
//     padding-right: 1rem;
//   }
// `;

const MobileToggle = styled.div`
  width: 3rem;
  padding: 0.5rem;
  align-self: center;
  cursor: pointer;
  transition: opacity 250ms ease-in-out;
  &:hover {
    opacity: 0.7;
  }

  @media screen and (min-width: 900px) {
    display: none;
  }
`;

const NavSpacer = styled.div`
  flex: 1;
`;

const query = graphql`
  query {
    menuMobileImage: file(relativePath: { eq: "menu_mobile.png" }) {
      childImageSharp {
        fluid(maxWidth: 62) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
    crossImage: file(relativePath: { eq: "cross.png" }) {
      childImageSharp {
        fluid(maxWidth: 22) {
          ...GatsbyImageSharpFluid_noBase64
        }
      }
    }
  }
`;

const HeaderModal = styled(ReactModal)`
  &__overlay {
    position: fixed;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    background-color: rgba(255, 255, 255, 0.95);
    z-index: 1100;
    transition: opacity 250ms ease-in-out, transform 250ms ease-in-out;
    transform: translateY(-100%);

    @media screen and (min-width: 900px) {
      display: none;
    }

    &.ReactModal__Overlay--after-open {
      opacity: 1;
      transform: translateY(0);
    }

    &.ReactModal__Overlay--before-close {
      opacity: 0;
      transform: translateY(-100%);
    }
  }

  &__content {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    outline: none;
    display: flex;
    flex-direction: column;
  }
`;

const MainLogo = styled(Link)`
  display: block;
  transition: opacity 250ms ease-in-out, top 250ms ease-in-out;
  position: absolute;
  top: ${(props) => (props["data-scroll"] ? 0 : "-100%")};
  left: 0;
  width: 16rem;

  &:hover {
    opacity: 0.7;
  }

  @media screen and (min-width: 900px) {
    top: 0;
    height: 100%;
  }
`;

const SubLogo = styled(Link)`
  display: block;
  transition: opacity 250ms ease-in-out, top 250ms ease-in-out;
  position: absolute;
  top: ${(props) => (props["data-scroll"] ? "-100%" : 0)};
  left: 0;
  width: 16rem;
  height: 100%;

  &:hover {
    opacity: 0.7;
  }

  @media screen and (min-width: 900px) {
    top: -100%;
  }
`;

const DesktopLogo = styled.a`
  display: none;
  transition: opacity 250ms ease-in-out, top 250ms ease-in-out;
  position: absolute;
  top: ${(props) => (props["data-scroll"] ? "-100%" : 0)};
  right: 2rem;
  width: 6rem;
  height: 100%;

  &:hover {
    opacity: 0.7;
  }

  @media screen and (min-width: 900px) {
    display: block;
  }
`;

// const TTImage = styled(Img)`
//   width: 7rem;
// `;

const LanguageBar = styled.div`
  flex: 1;
  justify-content: flex-end;
  align-items: center;
  margin-right: ${(props) => (props["data-scroll"] ? "-0.5rem" : "7rem")};
  transition: 250ms ease-in-out;
  display: none;

  @media screen and (min-width: 900px) {
    display: flex;
  }
`;

const LanguageSelect = styled(Link)`
  padding-left: 0.5rem;
  padding-right: 0.5rem;
  color: ${(props) => (props.active ? "#bca273" : "#333")};
`;

const Header = ({ pageContext, uri, isIndex, headerData }) => {
  const [atTop, setAtTop] = useState(true);
  const [open, setOpen] = useState(false);

  let { lang } = pageContext;
  if (!lang) {
    lang = "en-gb";
  }

  const linkPrefix = lang === "en-gb" ? "" : "/" + lang;

  useScrollPosition(({ currPos: { y } }) => {
    const nextAtTop = y > -64;
    if (nextAtTop !== atTop) {
      setAtTop(nextAtTop);
    }
  });

  const baseUri = uri
    .replace(/^\/trusttomorrow/, "")
    .replace(/^\/zh-hk/, "")
    .replace(/^\/zh-cn/, "");

  return (
    <StaticQuery
      query={`${query}`}
      render={(data) => {
        if (!data || !headerData) {
          return;
        }

        const {
          logo_image,
          logo_imageSharp,
          logo_2_image,
          logo_2_imageSharp,
          nav_menu_image,
          nav_menu_imageSharp,
        } = headerData;

        return (
          <>
            <OuterContainer>
              <InnerContainer
              // expanded={expanded}
              >
                <LogoContainer>
                  {logo_imageSharp && (
                    <MainLogo to={linkPrefix + "/"} data-scroll={false}>
                      <Img
                        style={{ height: "100%" }}
                        fluid={logo_imageSharp.childImageSharp.fluid}
                        fadeIn={false}
                        objectFit={"contain"}
                        alt={logo_image.alt}
                      />
                    </MainLogo>
                  )}
                  {logo_2_imageSharp && (
                    <SubLogo to={linkPrefix + "/"} data-scroll={false}>
                      <Img
                        style={{ height: "100%" }}
                        fluid={logo_imageSharp.childImageSharp.fluid}
                        fadeIn={false}
                        objectFit={"contain"}
                        alt={logo_image.alt}
                      />
                    </SubLogo>
                  )}
                </LogoContainer>
                {/*<LargeNav>*/}
                {/*<LargeNavLink*/}
                {/*  activeClassName={"active"}*/}
                {/*  to={linkPrefix + "/chairman-message/"}*/}
                {/*>*/}
                {/*  {lang === "en-gb" ? "Chairman's Message" : ""}*/}
                {/*  {lang === "zh-hk" ? "董事長致辭" : ""}*/}
                {/*  {lang === "zh-cn" ? "董事长致辞" : ""}*/}
                {/*</LargeNavLink>*/}
                {/*<LargeNavLink*/}
                {/*  activeClassName={"active"}*/}
                {/*  to={linkPrefix + "/timeline/"}*/}
                {/*>*/}
                {/*  {lang === "en-gb" ? "Timeline" : ""}*/}
                {/*  {lang === "zh-hk" ? "情繫香港百五載" : ""}*/}
                {/*  {lang === "zh-cn" ? "情系香江百五载" : ""}*/}
                {/*</LargeNavLink>*/}
                {/*<LargeNavLink*/}
                {/*  activeClassName={"active"}*/}
                {/*  to={linkPrefix + "/our-stories/"}*/}
                {/*  partiallyActive={true}*/}
                {/*>*/}
                {/*  {lang === "en-gb" ? "Our Stories" : ""}*/}
                {/*  {lang === "zh-hk" ? "我們的故事" : ""}*/}
                {/*  {lang === "zh-cn" ? "我们的故事" : ""}*/}
                {/*</LargeNavLink>*/}
                {/*<LargeNavLink*/}
                {/*  activeClassName={"active"}*/}
                {/*  to={linkPrefix + "/blogs/"}*/}
                {/*  partiallyActive={true}*/}
                {/*>*/}
                {/*  <TTImage*/}
                {/*    style={{ height: "100%" }}*/}
                {/*    fluid={nav_menu_imageSharp.childImageSharp.fluid}*/}
                {/*    objectFit={"contain"}*/}
                {/*  ></TTImage>*/}
                {/*</LargeNavLink>*/}
                {/*</LargeNav>*/}
                <NavSpacer />
                <LanguageBar data-scroll={!isIndex ? false : atTop}>
                  <LanguageSelect
                    active={lang === "en-gb" ? 1 : 0}
                    to={baseUri}
                  >
                    ENG
                  </LanguageSelect>
                  <LanguageSelect
                    active={lang === "zh-hk" ? 1 : 0}
                    to={"/zh-hk/" + baseUri}
                  >
                    繁
                  </LanguageSelect>
                  <LanguageSelect
                    active={lang === "zh-cn" ? 1 : 0}
                    to={"/zh-cn/" + baseUri}
                  >
                    简
                  </LanguageSelect>
                </LanguageBar>
                {logo_2_imageSharp && (
                  <DesktopLogo
                    href={"https://www.swire.com/"}
                    data-scroll={!isIndex ? false : atTop}
                  >
                    <Img
                      style={{ height: "100%" }}
                      fluid={logo_2_imageSharp.childImageSharp.fluid}
                      fadeIn={false}
                      objectFit={"contain"}
                      alt={logo_image.alt}
                    />
                  </DesktopLogo>
                )}
                <MobileToggle
                  onClick={() => {
                    setOpen(true);
                  }}
                >
                  <Img
                    fluid={data.menuMobileImage.childImageSharp.fluid}
                    objectFit={"contain"}
                  />
                </MobileToggle>
              </InnerContainer>
            </OuterContainer>
            <Spacer />
            <HeaderModal
              ariaHideApp={false}
              isOpen={open}
              onRequestClose={() => {
                setOpen(false);
              }}
              closeTimeoutMS={250}
            >
              <MobileMenu
                data={{
                  crossImage: data.crossImage,
                  logo_image,
                  logo_imageSharp,
                  logo_2_image,
                  logo_2_imageSharp,
                  nav_menu_image,
                  nav_menu_imageSharp,
                  pageContext,
                  uri,
                }}
                setOpen={setOpen}
              />
            </HeaderModal>
          </>
        );
      }}
    />
  );
};

export default Header;
